import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { API } from "@/lib/admin-constants";
import { Plus, Trash2, Loader2, MapPin, Pencil } from "lucide-react";

const LOCATION_TYPES = ["venue", "practice", "game", "area"];

const MapLocationsManager = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", location_type: "venue", marker_color: "#0C2E3C", address: "", lat: "", lng: "", description: "", is_public: true });

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/map/locations`, { withCredentials: true });
      setLocations(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const resetForm = () => { setForm({ name: "", location_type: "venue", marker_color: "#0C2E3C", address: "", lat: "", lng: "", description: "", is_public: true }); setEditing(null); };

  const openEdit = (loc) => {
    setEditing(loc);
    setForm({ name: loc.name, location_type: loc.location_type, marker_color: loc.marker_color || "#0C2E3C", address: loc.address || "", lat: String(loc.lat || ""), lng: String(loc.lng || ""), description: loc.description || "", is_public: loc.is_public !== false });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    const payload = { ...form, lat: parseFloat(form.lat) || 0, lng: parseFloat(form.lng) || 0 };
    try {
      if (editing) {
        await axios.put(`${API}/map/locations/${editing.location_id}`, payload, { withCredentials: true });
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/map/locations`, payload, { withCredentials: true });
        toast.success("Location added");
      }
      setSheetOpen(false); resetForm(); fetchLocations();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm("Delete this location?")) return;
    try {
      await axios.delete(`${API}/map/locations/${locationId}`, { withCredentials: true });
      toast.success("Deleted"); fetchLocations();
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cfc-primary" size={32} /></div>;

  return (
    <div data-testid="map-locations-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Map Locations</h1>
          <p className="text-sm text-cfc-text-muted mt-1">{locations.length} locations on the public map</p>
        </div>
        <Button className="bg-cfc-primary text-white" onClick={() => { resetForm(); setSheetOpen(true); }} data-testid="add-location-btn">
          <Plus size={16} className="mr-1.5" /> Add Location
        </Button>
      </div>

      <p className="text-sm text-cfc-text-muted bg-cfc-cream-alt border border-cfc-border p-3 rounded-sm mb-6">
        Add practice fields, game venues, and community areas. Use Google Maps to find latitude/longitude coordinates (right-click any point on Google Maps and click the coordinates to copy them).
      </p>

      {locations.length === 0 ? (
        <div className="text-center py-16 text-cfc-text-muted">
          <MapPin size={48} className="mx-auto mb-4 opacity-40" />
          <p className="text-lg font-medium mb-1">No locations yet</p>
          <p className="text-sm">Add venues, practice fields, and community areas to populate the public map.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {locations.map((loc) => (
            <div key={loc.location_id} className="flex items-center gap-4 bg-white border border-cfc-border rounded-sm p-3" data-testid={`location-row-${loc.location_id}`}>
              <MapPin size={18} className="text-cfc-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm text-cfc-primary">{loc.name}</h3>
                <p className="text-[11px] text-cfc-text-muted">{loc.location_type} &middot; {loc.address || "No address"} &middot; ({loc.lat}, {loc.lng})</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => openEdit(loc)}><Pencil size={14} /></Button>
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(loc.location_id)}><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      )}

      <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) resetForm(); }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading text-cfc-primary">{editing ? "Edit Location" : "Add Location"}</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-3">
            <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Name *</label><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Pen Park Field 3" className="mt-1" data-testid="loc-name" /></div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Type</label>
              <div className="flex gap-1.5 mt-1">
                {LOCATION_TYPES.map((t) => (
                  <button key={t} onClick={() => setForm((p) => ({ ...p, location_type: t }))} className={`px-2.5 py-1 text-[11px] font-bold rounded-sm border ${form.location_type === t ? "bg-cfc-primary text-white border-cfc-primary" : "border-cfc-border text-cfc-text-muted"}`} data-testid={`loc-type-${t}`}>{t}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Marker Color</label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={form.marker_color} onChange={(e) => setForm((p) => ({ ...p, marker_color: e.target.value }))} className="w-8 h-8 rounded cursor-pointer border border-cfc-border" data-testid="loc-color" />
                <Input value={form.marker_color} onChange={(e) => setForm((p) => ({ ...p, marker_color: e.target.value }))} className="flex-1 font-mono text-xs" />
                <div className="w-6 h-6 rounded-full border-2 border-white shadow-md" style={{ backgroundColor: form.marker_color }} />
              </div>
            </div>
            <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Address</label><Input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} placeholder="Full address" className="mt-1" data-testid="loc-address" /></div>
            <div>
              <label className="text-xs font-bold text-cfc-text-muted uppercase">Paste from Google Maps</label>
              <Input
                placeholder="Right-click on Google Maps → paste coords e.g. 38.0336, -78.5081"
                className="mt-1 bg-cfc-cream-alt"
                data-testid="loc-paste-coords"
                onChange={(e) => {
                  const val = e.target.value.trim();
                  const parts = val.split(/[,\s]+/).filter(Boolean);
                  if (parts.length >= 2) {
                    const lat = parseFloat(parts[0]);
                    const lng = parseFloat(parts[1]);
                    if (!isNaN(lat) && !isNaN(lng)) {
                      setForm((p) => ({ ...p, lat: String(lat), lng: String(lng) }));
                      e.target.value = "";
                      toast.success(`Coordinates set: ${lat}, ${lng}`);
                    }
                  }
                }}
              />
              <p className="text-[10px] text-cfc-text-muted mt-0.5">Paste the full coordinate pair — lat & lng auto-fill below</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Latitude *</label><Input value={form.lat} onChange={(e) => setForm((p) => ({ ...p, lat: e.target.value }))} placeholder="38.0293" className="mt-1" data-testid="loc-lat" /></div>
              <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Longitude *</label><Input value={form.lng} onChange={(e) => setForm((p) => ({ ...p, lng: e.target.value }))} placeholder="-78.4767" className="mt-1" data-testid="loc-lng" /></div>
            </div>
            <div><label className="text-xs font-bold text-cfc-text-muted uppercase">Description</label><Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className="mt-1" data-testid="loc-desc" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm((p) => ({ ...p, is_public: e.target.checked }))} className="accent-cfc-primary" />
              <label className="text-sm text-cfc-primary font-medium">Show on public map</label>
            </div>
            <Button className="w-full bg-cfc-primary text-white" onClick={handleSave} disabled={saving} data-testid="save-location-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} {editing ? "Update" : "Add"} Location
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MapLocationsManager;
